/**
 * voiceManager — provider-agnostic softphone facade.
 *
 * Delegates to either the Twilio or Telnyx backend manager, chosen at runtime from the
 * `provider` field on the voice token (`apiService.getVoiceToken()`). Both backends expose the same
 * surface (`VoiceBackend`), so React consumers (`VoiceCallProvider`) depend only on this facade and
 * are unaware of which provider is active.
 *
 * The backend is resolved lazily on first use and cached; subscribers attached before resolution
 * are bridged to the chosen backend's event stream once it is selected.
 */

import { Capacitor } from '@capacitor/core';
import { twilioVoiceManager } from '@/lib/twilioVoice';
import { telnyxVoiceManager } from '@/lib/telnyxVoice';
import { telnyxVoiceWebManager } from '@/lib/telnyxVoiceWeb';
import type { AudioDevice, AudioDeviceType, VoiceCallEvent, VoiceEventHandler } from '@/lib/twilioVoice';
import { apiService } from '@/lib/api';

export interface VoiceBackend {
  subscribe(handler: VoiceEventHandler): () => void;
  init(): Promise<void>;
  destroy(): Promise<void>;
  makeCall(toE164: string, callerIdE164: string, displayName?: string, jobId?: string): Promise<void>;
  makeMaskedCall(jobId: string, contactId: string, callerIdE164: string, which?: 'primary' | 'additional', displayName?: string): Promise<void>;
  /** Re-place the last call (one-tap Redial after an unclean drop). No-op on backends without it. */
  redial(): Promise<void>;
  endCall(callSid?: string): Promise<void>;
  muteCall(muted: boolean, callSid?: string): Promise<void>;
  getAudioDevices(): Promise<{ devices: AudioDevice[]; selected: AudioDeviceType }>;
  /** `id` addresses a specific device when several share one type (web only; native selects by type). */
  selectAudioDevice(type: AudioDeviceType, id?: string): Promise<void>;
  sendDigits(digits: string): Promise<void>;
  readonly currentState: VoiceCallEvent;
}

class VoiceManagerFacade {
  private static _instance: VoiceManagerFacade | null = null;
  static get instance(): VoiceManagerFacade {
    if (!VoiceManagerFacade._instance) VoiceManagerFacade._instance = new VoiceManagerFacade();
    return VoiceManagerFacade._instance;
  }

  private _backend: VoiceBackend | null = null;
  private _provider: 'twilio' | 'telnyx' | null = null;
  private _resolving: Promise<VoiceBackend> | null = null;
  private _listeners: VoiceEventHandler[] = [];
  private _currentState: VoiceCallEvent = { state: 'idle' };
  private _unsubBackend: (() => void) | null = null;
  /**
   * Event bridge for RECEIVE mode, independent of the outbound backend (review-7 #1): the
   * outbound provider is resolved from the SELECTED COMPANY's numbers, but ring targets are org
   * users — a user working in a Twilio-only company can still be targeted by a sibling company's
   * Telnyx DID, and their tab must ring. Receive therefore always bridges telnyxVoiceWebManager
   * directly; when the outbound backend later resolves to that same manager, this bridge is
   * dropped in favor of the backend one (never both — double emits).
   */
  private _unsubReceiveBridge: (() => void) | null = null;
  /**
   * Bumped by destroy() (logout / org switch). A backend resolution that was in flight when the
   * context changed must NOT complete against the new context: it would cache the OLD account's
   * provider/token and bridge its backend (review-4 critical). Receive enable/disable races are
   * guarded separately, inside the web manager's own receive generation.
   */
  private _facadeGen = 0;
  // NOTE: a `_receiveIntent` flag used to live here, re-checked after enable's backend
  // resolution await. Review-7 #1 removed that await (receive no longer resolves the outbound
  // backend), so enable/disable race protection now lives entirely in the web manager's own
  // receive generation and the flag was removed as dead state.

  subscribe(handler: VoiceEventHandler): () => void {
    this._listeners.push(handler);
    handler({ ...this._currentState });
    return () => {
      this._listeners = this._listeners.filter((l) => l !== handler);
    };
  }

  /**
   * Resolve (and cache) the backend by reading the token's provider.
   *
   * A failed probe is NOT cached: guessing a provider and sticking to it would strand a Telnyx
   * company on the Twilio backend for the rest of the page (isSupported() false → "no supported
   * voice number" on every call) after one transient error — e.g. the 503 the token endpoint
   * returns on a Telnyx blip. Rejecting lets the next call re-probe; every caller already handles it.
   *
   * Known residuals, both accepted because Twilio is obsolete and slated for removal (owner
   * decision, review-8 #5/#11) — they only matter in a mixed Telnyx+Twilio org, which is a
   * transitional state:
   * - The cache is per PAGE, not per company: a Telnyx↔Twilio company switch keeps the old
   *   OUTBOUND backend until reload (receive is unaffected — it has its own bridge). See also
   *   TODO(mixed-provider-softphone) on the token endpoint.
   * - When the probe resolves Twilio while a receive-mode call is ringing, the subscribe() below
   *   immediately emits Twilio's cached 'idle' over the facade, which can momentarily hide the
   *   incoming card. Fixing it means per-source event ownership in the bridge — not worth it for
   *   a provider that is being removed.
   */
  private resolveBackend(): Promise<VoiceBackend> {
    if (this._backend) return Promise.resolve(this._backend);
    if (!this._resolving) {
      const gen = this._facadeGen;
      const resolution: Promise<VoiceBackend> = (async () => {
        const tok = await apiService.getVoiceToken();
        // destroy() ran while the token request was in flight (logout / org switch): do not cache
        // the stale context's provider or bridge its backend — callers get a rejection and the
        // next use re-probes under the new context.
        if (gen !== this._facadeGen) throw new Error('Voice context changed during provider resolution.');
        const provider: 'twilio' | 'telnyx' = tok.provider === 'telnyx' ? 'telnyx' : 'twilio';
        // On web, the Telnyx backend is the browser JS-SDK manager (native uses the Capacitor plugin).
        // Twilio-on-web is unsupported and falls through to its no-op manager.
        const backend: VoiceBackend =
          provider === 'telnyx'
            ? (Capacitor.isNativePlatform() ? telnyxVoiceManager : telnyxVoiceWebManager)
            : twilioVoiceManager;
        // The receive bridge may already cover the web manager (receive enabled before the first
        // outbound use) — drop it before bridging as the backend, or every event emits twice.
        if (backend === telnyxVoiceWebManager && this._unsubReceiveBridge) {
          this._unsubReceiveBridge();
          this._unsubReceiveBridge = null;
        }
        // Bridge backend events to facade subscribers.
        this._unsubBackend = backend.subscribe((e) => this._emit(e));
        this._backend = backend;
        this._provider = provider;
        return backend;
      })().catch((err) => {
        // Clear only OUR OWN registration (review-6 #9): after a destroy() this slot may already
        // hold a NEWER context's resolution — nulling it unconditionally would let yet another
        // probe start in parallel, racing the live one and double-bridging backend events.
        if (this._resolving === resolution) this._resolving = null;
        throw err;
      });
      this._resolving = resolution;
    }
    return this._resolving;
  }

  async init(): Promise<void> {
    const b = await this.resolveBackend();
    await b.init();
  }

  /**
   * Full teardown for auth/org context changes (called on SignalR connection teardown — logout and
   * org switch). Resets the CACHED PROVIDER too (review-4): an account that migrates Twilio →
   * Telnyx (or a user switching between such orgs) must re-probe, or inbound registration never
   * starts / outbound sticks to the wrong provider. A softphone call that is live at this moment
   * is torn down with it — a call under the old account/org context must not continue past it.
   */
  async destroy(): Promise<void> {
    this._facadeGen++;
    if (this._unsubBackend) { this._unsubBackend(); this._unsubBackend = null; }
    if (this._unsubReceiveBridge) { this._unsubReceiveBridge(); this._unsubReceiveBridge = null; }
    const b = this._backend;
    this._backend = null;
    this._provider = null;
    this._resolving = null;
    // React consumers must not stay frozen on the dead context's last event (review-5 #2): with
    // the bridge unsubscribed above, nothing else would ever reset a lingering 'connected' state —
    // the call UI would keep rendering over the login screen and the one-call-at-a-time guard
    // (voiceManager.currentState !== 'idle') would refuse every dial for the next account.
    this._emit({ state: 'idle' });
    if (b) await b.destroy();
    // The web manager can hold a live receive session/socket even when it is NOT the resolved
    // backend (review-8 #1, a regression from decoupling receive from the outbound probe): on a
    // pure receiver tab the backend is null, and on a cross-company target it can be Twilio — in
    // both cases the line above never reached the Telnyx web manager, so the previous account's
    // registration kept ringing into the next login. Destroy it unconditionally on web; its
    // destroy() is an idempotent no-op when nothing is live.
    if (!Capacitor.isNativePlatform() && b !== telnyxVoiceWebManager) {
      await telnyxVoiceWebManager.destroy();
    }
  }

  /**
   * The active softphone provider, resolved from the voice token. Callers use this to pick a
   * caller-ID number of the matching provider (a Telnyx call must dial from a Telnyx number).
   */
  async getActiveProvider(): Promise<'twilio' | 'telnyx'> {
    await this.resolveBackend();
    return this._provider ?? 'twilio';
  }

  /**
   * Register this tab for inbound calls (web dialer receive mode; Telnyx web backend only).
   *
   * MUST go through the facade, not telnyxVoiceWebManager directly (review fix): VoiceCallProvider
   * subscribes to THIS facade, and the web manager's events must be bridged before the first
   * invite or the tab rings the SDK with no modal/ringtone/Answer. Deliberately does NOT resolve
   * the outbound backend (review-7 #1): that probe is COMPANY-scoped, and a ring target working
   * in a Twilio-only company would resolve Twilio and silently never register — while the caller
   * of this method (`isRingTarget`) already proves an ORG Telnyx DID targets this user. Receive
   * gets its own event bridge instead (see _unsubReceiveBridge).
   */
  async enableInboundReceive(): Promise<void> {
    if (Capacitor.isNativePlatform()) return; // desktop web only, by design
    if (!this._unsubReceiveBridge && this._backend !== telnyxVoiceWebManager) {
      this._unsubReceiveBridge = telnyxVoiceWebManager.subscribe((e) => this._emit(e));
    }
    await telnyxVoiceWebManager.enableReceiveMode();
  }

  /**
   * Unregister from inbound calls. Clears the intent FIRST so an in-flight enable's continuation
   * no-ops. Talks to the web manager unconditionally (mirrors enable — receive is independent of
   * the resolved outbound backend); disableReceiveMode() is a cheap no-op when receive is off.
   * The receive bridge stays until destroy(): re-enables reuse it, and an idle bridged manager
   * emits nothing.
   */
  async disableInboundReceive(): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    await telnyxVoiceWebManager.disableReceiveMode();
  }

  /**
   * An admin force-disconnected this user's receiving tabs (WebDialerSessionsEnded reached this
   * tab via its user group). Like disable, but the web manager also latches receive mode off
   * until reload/context change — otherwise the periodic re-sync would re-register the tab and
   * undo the kick. The latch is set on the web manager UNCONDITIONALLY (not just when it is the
   * resolved backend): a fresh tab still mid-startup has no backend resolved yet, and without the
   * latch its first registration sync would sail through moments after the kick (review-6 #2).
   */
  async remoteDisconnectInboundReceive(): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    await telnyxVoiceWebManager.markRemoteDisconnected();
  }

  /** User choice (indicator menu): stop receiving on THIS tab until resumed or reload. */
  async pauseInboundReceive(): Promise<void> {
    if (Capacitor.isNativePlatform()) return;
    await telnyxVoiceWebManager.pauseReceive();
  }

  /** Clears the pause latch; the caller re-runs the registration sync to re-register. */
  resumeInboundReceive(): void {
    if (Capacitor.isNativePlatform()) return;
    telnyxVoiceWebManager.resumeReceive();
  }

  async makeCall(toE164: string, callerIdE164: string, displayName?: string, jobId?: string): Promise<void> {
    const b = await this.resolveBackend();
    await b.makeCall(toE164, callerIdE164, displayName, jobId);
  }

  async makeMaskedCall(jobId: string, contactId: string, callerIdE164: string, which: 'primary' | 'additional' = 'primary', displayName?: string): Promise<void> {
    const b = await this.resolveBackend();
    await b.makeMaskedCall(jobId, contactId, callerIdE164, which, displayName);
  }

  async redial(): Promise<void> {
    const b = await this.resolveBackend();
    await b.redial();
  }

  /**
   * The backend that owns the CURRENT call, when that differs from the resolved outbound one
   * (review-7 #1): an inbound receive-mode call lives on the Telnyx web manager even when the
   * selected company's outbound provider resolved to Twilio (whose web manager is a no-op — End/
   * Mute/DTMF would silently do nothing). Null = no special ownership; use the resolved backend.
   */
  private _webCallOwner(): VoiceBackend | null {
    if (!Capacitor.isNativePlatform() && telnyxVoiceWebManager.currentState.state !== 'idle') {
      return telnyxVoiceWebManager;
    }
    return null;
  }

  async endCall(callSid?: string): Promise<void> {
    const b = this._webCallOwner() ?? await this.resolveBackend();
    await b.endCall(callSid);
  }

  async muteCall(muted: boolean, callSid?: string): Promise<void> {
    const b = this._webCallOwner() ?? await this.resolveBackend();
    await b.muteCall(muted, callSid);
  }

  async getAudioDevices(): Promise<{ devices: AudioDevice[]; selected: AudioDeviceType }> {
    const b = this._webCallOwner() ?? await this.resolveBackend();
    return b.getAudioDevices();
  }

  async selectAudioDevice(type: AudioDeviceType, id?: string): Promise<void> {
    const b = this._webCallOwner() ?? await this.resolveBackend();
    await b.selectAudioDevice(type, id);
  }

  /**
   * True when this platform can place softphone calls: always on native, and on web only when the
   * active provider is Telnyx (Twilio-on-web is unsupported). UI gates the web softphone on this AND a
   * desktop viewport (mobile browsers keep their existing softphone-less behavior).
   */
  async isSupported(): Promise<boolean> {
    if (Capacitor.isNativePlatform()) return true;
    return (await this.getActiveProvider()) === 'telnyx';
  }

  sendDigits(digits: string): void {
    // Fire-and-forget: a call is active by the time DTMF is sent. Prefer the call's owner — an
    // inbound call can live on the web manager while the outbound backend is unresolved/Twilio.
    const owner = this._webCallOwner();
    if (owner) { void owner.sendDigits(digits); return; }
    void this.resolveBackend().then((b) => b.sendDigits(digits));
  }

  get currentState(): VoiceCallEvent { return { ...this._currentState }; }

  private _emit(event: VoiceCallEvent): void {
    this._currentState = event;
    this._listeners.forEach((l) => l({ ...event }));
  }
}

export const voiceManager = VoiceManagerFacade.instance;
